import { ScrollTrack, getDriver, clamp01, REDUCED } from "@theme/1scp-scroll";
import { ThemeEvents } from "@theme/events";
import { rollText, rolledText, amount, fold, foldHeight, fadeToggle, foldShown, FOLD_TIME } from "@theme/1scp-roll";

const ref = (root, name) => root.querySelector(`[data-scpd-ref="${name}"]`);

const refs = (root, name) => [...root.querySelectorAll(`[data-scpd-ref="${name}"]`)];

const RING = 44;

// Values that change with the variant roll to their new text; a price rolls down when it drops.
// Empty text is written straight away, because its row is hidden with it.
const swapText = (cell, text) => {
  if (!text) {
    cell.textContent = "";
    return;
  }
  const before = rolledText(cell);
  const cheaper = amount(text) && amount(text) < amount(before);
  rollText(cell, text, cheaper ? -1 : 1);
};



// The bag is held back until the button has answered (see `ADDED_PAUSE` in the cart), otherwise it
// slides over the button before the tick can be seen.
let pressed = null;
const holds = new WeakMap();

document.addEventListener(
  "click",
  (event) => {
    const button = event.target.closest?.("[type='submit']");
    if (!button?.closest("scpd-pdp-hero, scpd-buy-bar") || button.closest("scpd-waitlist") || button.disabled) return;
    pressed = button;
    button.classList.add("is-adding");
    // A network failure sends no event at all, so the spinner also stops on its own.
    clearTimeout(holds.get(button)?.stall);
    const stall = setTimeout(() => {
      button.classList.remove("is-adding");
      // Forgotten too, or the next cart update from elsewhere would play its "Added" on this button.
      if (pressed === button) pressed = null;
    }, 8000);
    holds.set(button, { ...holds.get(button), stall });
  },
  true
);

document.addEventListener(ThemeEvents.cartError, () => {
  pressed?.classList.remove("is-adding");
  pressed = null;
});

document.addEventListener(ThemeEvents.cartUpdate, (event) => {
  const data = event.detail?.data ?? {};
  const button = pressed;
  if (!button?.isConnected || data.source === "scpd-cart") return;
  pressed = null;
  button.classList.remove("is-adding");
  clearTimeout(holds.get(button)?.stall);
  if (!data.isIncremental || data.didError) return;

  const host = button.closest("[data-added-label]");
  const label = button.querySelector(".scpd-buy__label, .scpd-buy-bar__label");

  const original = holds.get(button)?.original ?? (label?.dataset.scpdRollTo || label?.textContent.trim());
  clearTimeout(holds.get(button)?.timer);
  button.classList.add("is-added");
  rollText(label, host?.dataset.addedLabel);

  const timer = setTimeout(() => {
    button.classList.remove("is-added");
    rollText(label, holds.get(button)?.original ?? original);
    holds.delete(button);
  }, 2600);
  holds.set(button, { original, timer });
});

// A label still showing "Added" takes the new text once it rolls back.
const setLabel = (button, text) => {
  const cell = button.querySelector(".scpd-buy__label, .scpd-buy-bar__label");
  if (!cell || !text) return;
  const hold = holds.get(button);
  if (hold?.timer) hold.original = text;
  else rollText(cell, text);
};

// A native `<select>` cannot carry this design's two type styles and colour dot, so this is a custom menu.
// It fires a plain bubbling `change`, so the variant logic treats it exactly like a `<select>`.
class OptionSelect extends HTMLElement {
  #trigger = null;
  #panel = null;
  #options = [];

  connectedCallback() {
    this.#trigger = this.querySelector(".scpd-select__trigger");
    this.#panel = this.querySelector(".scpd-select__panel");
    this.#options = [...this.querySelectorAll(".scpd-select__option")];
    if (!this.#trigger || !this.#panel) return;

    this.#syncTrigger();
    this.#trigger.addEventListener("click", this.#toggle);
    this.#trigger.addEventListener("keydown", this.#onTriggerKey);
    this.#panel.addEventListener("click", this.#onPanelClick);
    this.#panel.addEventListener("keydown", this.#onPanelKey);
  }

  disconnectedCallback() {
    this.#unbindOutside();
  }

  get value() {
    return this.#selected?.dataset.value ?? "";
  }

  get valueId() {
    return this.#selected?.dataset.optionValueId ?? "";
  }

  // Matched by option value id, never by position.
  syncAvailability(source) {
    for (const option of this.#options) {
      const twin = source.querySelector(
        `.scpd-select__option[data-option-value-id="${option.dataset.optionValueId}"]`
      );
      if (twin) option.classList.toggle("is-unavailable", twin.classList.contains("is-unavailable"));
    }
  }

  get swatch() {
    return this.#selected?.dataset.swatch ?? "";
  }

  get #selected() {
    return this.#options.find((option) => option.classList.contains("is-selected"));
  }

  get #isOpen() {
    return this.hasAttribute("open");
  }

  #toggle = () => (this.#isOpen ? this.#close() : this.#open());

  #open() {
    if (this.#isOpen) return;
    this.setAttribute("open", "");
    this.#panel.hidden = false;
    this.#trigger.setAttribute("aria-expanded", "true");
    (this.#selected ?? this.#options[0])?.focus();

    // Bound on open only, so a page of menus costs one listener, not one each.
    document.addEventListener("pointerdown", this.#onOutside, true);
  }

  #close({ focusTrigger = false } = {}) {
    if (!this.#isOpen) return;
    this.removeAttribute("open");
    this.#panel.hidden = true;
    this.#trigger.setAttribute("aria-expanded", "false");
    this.#unbindOutside();
    if (focusTrigger) this.#trigger.focus();
  }

  #unbindOutside() {
    document.removeEventListener("pointerdown", this.#onOutside, true);
  }

  #onOutside = (event) => {
    if (!this.contains(event.target)) this.#close();
  };

  #onTriggerKey = (event) => {
    if (!["ArrowDown", "ArrowUp", "Enter", " "].includes(event.key)) return;
    event.preventDefault();
    this.#open();
  };

  #onPanelKey = (event) => {
    const index = this.#options.indexOf(document.activeElement);

    switch (event.key) {
      case "ArrowDown":
        event.preventDefault();
        this.#options[Math.min(this.#options.length - 1, index + 1)]?.focus();
        break;
      case "ArrowUp":
        event.preventDefault();
        this.#options[Math.max(0, index - 1)]?.focus();
        break;
      case "Home":
        event.preventDefault();
        this.#options[0]?.focus();
        break;
      case "End":
        event.preventDefault();
        this.#options.at(-1)?.focus();
        break;
      case "Enter":
      case " ":
        event.preventDefault();
        if (index >= 0) this.#pick(this.#options[index]);
        break;
      case "Escape":
        event.preventDefault();
        this.#close({ focusTrigger: true });
        break;
      case "Tab":
        this.#close();
        break;
    }
  };

  #onPanelClick = (event) => {
    const option = event.target.closest(".scpd-select__option");
    if (option) this.#pick(option);
  };

  select(value) {
    const match = this.#options.find((option) => option.dataset.value === value);
    if (match) this.#pick(match, { announce: false });
  }

  offers(value) {
    return this.#options.some((option) => option.dataset.value === value);
  }

  choose(value) {
    const match = this.#options.find((option) => option.dataset.value === value);
    if (match) this.#pick(match, { focus: false });
  }

  #pick(option, { announce = true, focus = true } = {}) {
    const changed = option !== this.#selected;

    for (const candidate of this.#options) {
      const on = candidate === option;
      candidate.classList.toggle("is-selected", on);
      candidate.setAttribute("aria-selected", String(on));
    }

    this.#syncTrigger();
    this.#close({ focusTrigger: announce && focus });

    if (changed && announce) this.dispatchEvent(new Event("change", { bubbles: true }));
  }

  #syncTrigger() {
    const option = this.#selected;
    if (!option) return;

    const name = this.#trigger.querySelector(".scpd-select__name");
    if (name) rollText(name, option.dataset.value ?? "", 1, { resize: true });

    const note = this.#trigger.querySelector(".scpd-select__note");
    if (note) {
      if (option.dataset.note) rollText(note, option.dataset.note, 1, { resize: true });
      else note.textContent = "";
    }

    const dot = this.#trigger.querySelector(".scpd-select__dot");
    if (dot) dot.style.setProperty("--scpd-swatch", option.dataset.swatch || "transparent");
  }
}

class PdpHero extends ScrollTrack {
  #variant = null;
  #panes = [];
  #dots = [];
  #index = -1;
  #stageSize = null;
  #request = null;
  #footerIo = null;
  #dockRun = null;
  #planOn = null;

  connectedCallback() {
    super.connectedCallback();

    this.#panes = [...this.querySelectorAll(".scpd-pdp__pane")];
    this.#dots = [...this.querySelectorAll("[data-scpd-dot]")];

    // On the document, because pickers for the same option can sit in more than one section and must agree.
    document.addEventListener("change", this.#onOptionChange);

    for (const dot of this.#dots) {
      dot.addEventListener("click", this.#onDotClick);
    }

    // A submit carrying a plan is the subscription purchase; filling the field here keeps one form for both buttons.
    this.addEventListener("click", this.#onButtonClick, true);

    this.#frame?.addEventListener("scroll", this.#onRailScroll, { passive: true });

    this.#stageSize = new ResizeObserver(this.#fit);
    const stage = this.querySelector(".scpd-pdp__stage");
    if (stage) this.#stageSize.observe(stage);
    addEventListener("resize", this.#fit, { passive: true });
    this.#fit();


    const dock = this.querySelector(".scpd-pdp__dock");
    const footer = document.querySelector(".shopify-section-group-footer-group");
    // The layout class sits on the surrounding section, not on this element.
    if (dock && footer && this.closest(".scpd-pdp--editorial")) {
      this.#footerIo = new IntersectionObserver(([entry]) => {
        dock.classList.toggle("is-away", entry.isIntersecting);
        dock.toggleAttribute("inert", entry.isIntersecting);
      });
      this.#footerIo.observe(footer);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener("change", this.#onOptionChange);
    removeEventListener("resize", this.#fit);
    this.#stageSize?.disconnect();
    this.#footerIo?.disconnect();
    this.#frame?.removeEventListener("scroll", this.#onRailScroll);
    this.#request?.abort();
  }

  get #pickers() {
    return [...document.querySelectorAll("scpd-select[data-scpd-option]")];
  }

  get #frame() {
    return this.querySelector(".scpd-pdp__frame");
  }

  // A sticky element taller than the screen hides its foot, so `top` is offset by the overflow.
  // CSS cannot do this itself because a percentage `top` measures the container, not the element.
  #fit = () => {
    const stage = this.querySelector(".scpd-pdp__stage");
    if (!stage) return;
    // Only a sticky stage is offset; on a relative one the same `top` would shift the block up under the header.
    if (getComputedStyle(stage).position !== "sticky") {
      stage.style.top = "";
      return;
    }
    stage.style.top = `${Math.min(0, innerHeight - stage.offsetHeight)}px`;
  };

  #readVariant(root) {
    const tag = root.querySelector("[data-scpd-variant]");
    if (!tag) return undefined;
    try {
      return JSON.parse(tag.textContent);
    } catch {
      // A malformed block must not take the buy button down with it.
      return undefined;
    }
  }

  #takeGallery(root) {
    const fresh = root.querySelector("[data-scpd-gallery]");
    const current = this.querySelector("[data-scpd-gallery]");
    if (!fresh || !current || fresh.innerHTML === current.innerHTML) return;

    this.#frame?.removeEventListener("scroll", this.#onRailScroll);
    for (const dot of this.#dots) dot.removeEventListener("click", this.#onDotClick);

    // The whole strip is kept as a still, not just one picture: between two panes the frame shows
    // parts of both, and a single picture laid over the frame would not line up with them. The copy
    // keeps the strip's class, so it stays where the scroll puts the real strip.
    const leaving = current.querySelector(".scpd-pdp__strip");
    const still = leaving && !REDUCED.matches ? leaving.cloneNode(true) : null;
    if (still) {
      still.classList.add("scpd-pdp__strip--still");
      still.setAttribute("aria-hidden", "true");
      for (const img of still.querySelectorAll("img")) {
        img.loading = "eager";
        img.alt = "";
      }
    }

    // On a phone the frame scrolls sideways instead, and a new frame would start back at the first picture.
    const scrolled = current.querySelector(".scpd-pdp__frame")?.scrollLeft || 0;
    current.innerHTML = fresh.innerHTML;
    const frame = current.querySelector(".scpd-pdp__frame");
    if (frame && scrolled) frame.scrollLeft = scrolled;

    this.#panes = [...this.querySelectorAll(".scpd-pdp__pane")];
    this.#dots = [...this.querySelectorAll("[data-scpd-dot]")];
    for (const dot of this.#dots) dot.addEventListener("click", this.#onDotClick);
    this.#frame?.addEventListener("scroll", this.#onRailScroll, { passive: true });

    this.#index = -1;
    const on = Number.parseFloat(this.style.getPropertyValue("--scpd-strip")) || 0;
    this.#show(Math.min(this.#panes.length - 1, Math.round(on)));

    if (still) this.#crossfade(still, on);
  }

  async #crossfade(still, on) {
    const frame = this.querySelector(".scpd-pdp__frame");
    const strip = frame?.querySelector(".scpd-pdp__strip:not(.scpd-pdp__strip--still)");
    if (!frame || !strip) return;
    // Under the new strip: the new pictures fade in on top of the old ones, which only give way
    // afterwards, so the frame never thins out to the page between the two. The old ones do go all
    // the way, or they would show through a cut-out pouch.
    frame.prepend(still);
    strip.style.opacity = "0";

    // The panes in view are the ones waited for, so they load first and at once, lazy or not.
    const seen = [Math.floor(on), Math.ceil(on)]
      .map((index) => this.#panes[index]?.querySelector("img"))
      .filter(Boolean);
    for (const img of seen) {
      img.loading = "eager";
      img.fetchPriority = "high";
    }
    // Held until they can be drawn, but never for long on a slow connection.
    await Promise.race([
      Promise.all(seen.map((img) => img.decode().catch(() => {}))),
      new Promise((done) => setTimeout(done, 1200)),
    ]);

    strip.style.opacity = "";
    strip.animate([{ opacity: 0 }, { opacity: 1 }], { duration: 700, easing: "cubic-bezier(0.25, 0.1, 0.25, 1)" });
    still
      .animate([{ opacity: 1 }, { opacity: 0 }], {
        duration: 600,
        delay: 350,
        easing: "cubic-bezier(0.45, 0, 0.55, 1)",
        fill: "forwards",
      })
      .finished.catch(() => {})
      .finally(() => still.remove());
  }

  write(progress) {
    const count = this.#panes.length;
    if (count < 2) return;

    // The value is continuous, not rounded, so the mask can show one pane leaving while the next arrives.
    const offset = progress * (count - 1);
    this.style.setProperty("--scpd-strip", offset.toFixed(4));
    this.#show(Math.round(offset));
  }

  #show(index) {
    if (index === this.#index) return;
    this.#index = index;
    this.#dots.forEach((dot, i) => dot.classList.toggle("is-active", i === index));
  }

  #onDotClick = (event) => {
    const index = Number(event.currentTarget.dataset.scpdDot);
    const frame = this.#frame;

    if (frame && frame.scrollWidth > frame.clientWidth) {
      frame.scrollTo({ left: index * frame.clientWidth, behavior: "smooth" });
      return;
    }

    // On the pinned stage the gallery follows scroll position, so selecting an image means scrolling to it.
    const count = this.#panes.length;
    const rect = this.getBoundingClientRect();
    const travel = rect.height - innerHeight;
    if (travel <= 0 || count < 2) return;

    const target = rect.top + scrollY + travel * (index / (count - 1));
    const lenis = getDriver().lenis;
    if (lenis) lenis.scrollTo(target);
    else scrollTo({ top: target, behavior: "smooth" });
  };

  #onRailScroll = () => {
    const frame = this.#frame;
    if (!frame || !frame.clientWidth) return;
    this.#show(Math.round(frame.scrollLeft / frame.clientWidth));
  };

  #onOptionChange = (event) => {
    const source = event.target.closest?.("scpd-select[data-scpd-option]");
    if (!source) return;

    const pickers = this.#pickers;

    // Mirror onto the other pickers before reading, so the page never shows two answers to one question.
    for (const peer of pickers) {
      if (peer !== source && peer.dataset.scpdOption === source.dataset.scpdOption) {
        peer.select(source.value);
      }
    }

    // One option value id per option, in option order: Shopify resolves the variant from that order.
    const positions = [...new Set(pickers.map((p) => Number(p.dataset.scpdOption)))].sort(
      (a, b) => a - b
    );
    const ids = positions
      .map((position) => pickers.find((p) => Number(p.dataset.scpdOption) === position).valueId)
      .filter(Boolean);

    this.#load(ids);
  };

  // A newer choice aborts the request in flight, so a quick run of clicks always ends on the last one.
  async #load(ids) {
    const { sectionId, productUrl } = this.dataset;
    if (!sectionId || !productUrl || !ids.length) return;

    this.#request?.abort();
    const request = new AbortController();
    this.#request = request;
    this.setAttribute("aria-busy", "true");

    try {
      const url = new URL(productUrl, location.origin);
      url.searchParams.set("section_id", sectionId);
      url.searchParams.set("option_values", ids.join(","));

      const response = await fetch(url, { signal: request.signal });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const html = new DOMParser().parseFromString(await response.text(), "text/html");

      for (const picker of this.#pickers) {
        const twin = html.querySelector(
          `scpd-select[data-scpd-option="${picker.dataset.scpdOption}"]`
        );
        if (twin) picker.syncAvailability(twin);
      }

      this.#takeGallery(html);

      const variant = this.#readVariant(html);
      if (variant !== undefined) this.#applyVariant(variant);
    } catch (error) {
      if (error.name !== "AbortError") console.error(error);
    } finally {
      if (this.#request === request) this.removeAttribute("aria-busy");
    }
  }

  #applyVariant(variant) {
    const form = this.querySelector('[ref="variantId"]');
    const buy = this.querySelector(".scpd-buy--solid");

    if (!variant) {
      // A combination the shop does not carry: say so on the button rather than leave the previous variant armed.
      if (buy) {
        buy.disabled = true;
        setLabel(buy, this.dataset.unavailableLabel || "Unavailable");
      }
      // The form still holds the last variant's id, so Subscribe would sign up for that one.
      const plan = this.querySelector(".scpd-buy--outline");
      if (plan) plan.disabled = true;
      // Tell the buy bar too, or it stays armed with the last valid variant id.
      document.dispatchEvent(new CustomEvent("scpd:variant", { detail: null }));
      return;
    }

    if (form) form.value = variant.id;

    for (const sku of refs(this, "sku")) swapText(sku, variant.sku || "");
    const number = this.querySelector(".scpd-pdp__number");
    if (number) number.hidden = !variant.sku;

    const facts = { servings: variant.servings, protein: variant.protein, "net-weight": variant.netWeight, reference: variant.sku };
    for (const [name, value] of Object.entries(facts)) {
      const cell = ref(this, name);
      if (!cell) continue;
      swapText(cell, value || "");
      const row = cell.closest(".scpd-pdp-specs__row");
      if (row) row.hidden = !value;
    }

    // `null` means the product has one fixed text for every flavour.
    const ingredients = ref(this, "ingredients");
    if (ingredients && typeof variant.ingredients === "string") {
      ingredients.innerHTML = variant.ingredients;
    }

    const flavour = ref(this, "flavour");
    const flavourAt = Number(this.dataset.flavourOption);
    if (flavour && flavourAt) swapText(flavour, variant.options[flavourAt - 1] ?? "");

    for (const cell of refs(this, "price")) swapText(cell, variant.price);

    if (variant.planPrice) {
      for (const cell of refs(this, "plan-price")) swapText(cell, variant.planPrice);
    }
    if (variant.planId) this.#armPlan(String(variant.planId));

    this.#variant = variant;
    this.#showBuyPrice();
    this.#showPlan(Boolean(variant.planPrice));

    if (buy) {
      buy.disabled = !variant.available;
      setLabel(buy, variant.available ? this.#buyLabel : this.#soldOutLabel);
    }

    const plan = this.querySelector(".scpd-buy--outline");
    if (plan) plan.disabled = !variant.available;

    document.dispatchEvent(new CustomEvent("scpd:variant", { detail: variant }));

    // `replaceState`, not `pushState`: picking a size is not a page visit.
    const url = new URL(location.href);
    url.searchParams.set("variant", variant.id);
    history.replaceState({}, "", url);
  }

  get #buyLabel() {
    return this.dataset.buyLabel || "";
  }

  get #soldOutLabel() {
    return this.dataset.soldoutLabel || this.#buyLabel;
  }

  // The plan comes with the variant, so Subscribe signs up for the chosen size, not the one the page opened on.
  #armPlan(plan) {
    const option = this.querySelector('[data-scpd-purchase]:not([data-scpd-purchase="once"])');
    if (option) option.dataset.scpdPurchase = plan;
    for (const button of this.querySelectorAll("[data-scpd-plan]")) button.dataset.scpdPlan = plan;
  }

  #onPurchase(option) {
    for (const peer of this.querySelectorAll("[data-scpd-purchase]")) {
      const on = peer === option;
      peer.classList.toggle("is-chosen", on);
      peer.setAttribute("aria-pressed", String(on));
    }

    const buy = this.querySelector(".scpd-buy--solid");
    const plan = option.dataset.scpdPurchase;
    if (buy) {
      if (plan && plan !== "once") buy.dataset.scpdPlan = plan;
      else delete buy.dataset.scpdPlan;
    }
    this.#showBuyPrice();
  }

  // While the plan toggle folds, the dock is pinned by its left edge instead of its centre, so its
  // width can change without anything inside it moving; before or after, it glides as one piece
  // between that edge and the centre. `left` is resolved against the dock's containing block, which
  // an ancestor's transform can make something other than the viewport, so it is read back, not assumed.
  async #foldPlan(purchase, onPlan) {
    const dock = this.querySelector(".scpd-pdp__dock");
    const pull = () => {
      const next = purchase.nextElementSibling;
      const gap = parseFloat(getComputedStyle(purchase.parentElement).columnGap) || 0;
      return next ? gap + (parseFloat(getComputedStyle(next).marginLeft) || 0) : 0;
    };
    if (!dock || getComputedStyle(dock).position !== "fixed" || REDUCED.matches) {
      fold(purchase, onPlan, pull);
      return;
    }

    const run = (this.#dockRun = Symbol());
    const centre = parseFloat(getComputedStyle(dock).left);
    const edgeFor = (width) => `${centre - width / 2}px`;
    const glide = (from, to) =>
      dock
        .animate([{ left: from }, { left: to }], { duration: 400, easing: "cubic-bezier(0.65, 0, 0.35, 1)" })
        .finished.catch(() => {});

    const current = dock.offsetWidth;
    let full = current;
    if (onPlan) {
      // A hide still running would be measured half closed.
      purchase.getAnimations().forEach((animation) => animation.cancel());
      purchase.hidden = false;
      full = dock.offsetWidth;
      purchase.hidden = true;
    }

    dock.style.transition = "none";
    dock.style.transform = "none";
    dock.style.left = edgeFor(current);

    // Coming back, the dock first glides to where the full dock's left edge will be.
    if (onPlan) {
      await glide(edgeFor(current), edgeFor(full));
      if (run !== this.#dockRun) return;
      dock.style.left = edgeFor(full);
    }

    fold(purchase, onPlan, pull);
    await new Promise((done) => setTimeout(done, FOLD_TIME + 30));
    if (run !== this.#dockRun) return;

    if (!onPlan) {
      const pinned = dock.style.left;
      await glide(pinned, edgeFor(dock.offsetWidth));
      if (run !== this.#dockRun) return;
    }

    dock.style.left = dock.style.transform = "";
    // The hand-back to the stylesheet's centring is committed before the transition returns;
    // otherwise the browser applies both in the same frame and slides the dock in from `left: 50%`.
    getComputedStyle(dock).transform;
    dock.getBoundingClientRect();
    requestAnimationFrame(() =>
      requestAnimationFrame(() => {
        if (run === this.#dockRun) dock.style.transition = "";
      })
    );
  }

  // A size off the plan (such as a travel pack) hides the choice and falls back to one-time.
  #showPlan(onPlan) {
    const purchase = this.querySelector(".scpd-pdp__purchase");
    if (purchase) {
      const once = purchase.querySelector('[data-scpd-purchase="once"]');
      if (!onPlan && once && !once.classList.contains("is-chosen")) this.#onPurchase(once);
      // In the dock the form after the toggle is pulled in by a margin that only applies while the
      // toggle shows, so that margin and the row's gap close together with it.
      // Kept apart from `hidden`, which only follows once the fold and the dock's glide have run.
      this.#planOn ??= foldShown(purchase);
      if (this.#planOn !== onPlan) {
        this.#planOn = onPlan;
        this.#foldPlan(purchase, onPlan);
      }
    }
    for (const element of this.querySelectorAll(".scpd-buy--outline")) element.hidden = !onPlan;
    // The subscription line in the pre-sale note folds up or down, and the note beside the price
    // fades, so a size off the plan (a travel pack) changes the words without a jump.
    for (const element of this.querySelectorAll(".scpd-pdp__plan-sentence")) foldHeight(element, onPlan);
    for (const element of this.querySelectorAll(".scpd-pdp__plan-note")) fadeToggle(element, onPlan);
  }

  #showBuyPrice() {
    const buy = this.querySelector(".scpd-buy--solid");
    const cell = ref(this, "buy-price");
    if (!buy || !cell) return;

    const price = this.#variant?.price ?? ref(this, "price")?.textContent;
    const planPrice = this.#variant?.planPrice ?? ref(this, "plan-price")?.textContent;
    const subscribed = Boolean(buy.dataset.scpdPlan);
    const text = subscribed && planPrice ? planPrice : price;
    if (!text) return;
    rollText(cell, text, amount(text) < amount(rolledText(cell)) ? -1 : 1);
  }

  #onButtonClick = (event) => {
    const purchase = event.target.closest("[data-scpd-purchase]");
    if (purchase) {
      this.#onPurchase(purchase);
      return;
    }

    const button = event.target.closest("[type='submit']");
    if (!button) return;

    const field = ref(this, "selling-plan");
    if (field) field.value = button.dataset.scpdPlan || "";
  };
}


// The dot follows the viewport's eye line, not section progress, so each station lights in front of the reader.
// The line grows through a mask's dash offset, because animating the dotted line's own dash would make the dots crawl.
class Intake extends ScrollTrack {
  #route = null;
  #reveal = null;
  #dot = null;
  #shapes = [];
  #leaders = [];
  #panels = [];
  #stations = [];
  #samples = [];
  #length = 0;
  #reached = -1;
  #svgTop = 0;
  #scale = 1;
  #narrow = false;
  #railDot = null;
  #railFill = null;
  #railTop = 0;
  #railHeight = 0;
  #railHoles = [];
  #railLine = null;
  #gridTop = 0;
  #badgeRadius = 0;

  // The route runs past where the track's progress saturates, so this section is driven by the viewport instead.
  get followsViewport() {
    return true;
  }

  measure() {
    // This breakpoint must match the one the stylesheet switches on.
    this.#narrow = !matchMedia("(min-width: 900px)").matches;

    this.#route = this.querySelector("[data-scpd-route]");
    this.#reveal = this.querySelector("[data-scpd-reveal]");
    this.#dot = this.querySelector("[data-scpd-journey-dot]");
    this.#leaders = [...this.querySelectorAll("[data-scpd-organ-label]")];
    this.#panels = [...this.querySelectorAll("[data-scpd-organ]")];

    this.#shapes = [...this.querySelectorAll(".scpd-intake__journey [data-scpd-organ-shape]")];

    // A hidden path has no measurable length.
    this.#length = this.#route?.getTotalLength?.() || 0;

    // Cached, so a frame reads no layout.
    const svg = this.querySelector(".scpd-intake__journey");
    const box = svg?.getBoundingClientRect();
    this.#svgTop = box ? box.top + scrollY : 0;
    this.#scale = box && box.height ? box.height / 1400 : 1;

    // The route only runs downwards, so the samples are sorted by construction.
    this.#samples = [];
    if (this.#length) {
      const steps = 200;
      for (let i = 0; i <= steps; i += 1) {
        const at = (i / steps) * this.#length;
        this.#samples.push({ y: this.#route.getPointAtLength(at).y, at });
      }
    }

    this.#stations = this.#shapes.map((shape) => {
      const match = /translate\(\s*(-?[\d.]+)[ ,]+(-?[\d.]+)/.exec(shape.getAttribute("transform") || "");
      return match ? Number(match[2]) : 0;
    });

    // Panels are written left gutter first, so pair them to stations by y, not by document order.
    this.#panels.sort(
      (a, b) => Number.parseFloat(a.style.top) - Number.parseFloat(b.style.top)
    );

    this.#measureRail();

    this.#reached = -1;
  }

  #measureRail() {
    this.#railDot = this.querySelector("[data-scpd-rail-dot]");
    this.#railFill = this.querySelector("[data-scpd-rail-fill]");
    this.#railHeight = 0;

    const grid = this.querySelector(".scpd-intake__grid");
    const line = this.querySelector("[data-scpd-rail-line]");
    if (!this.#narrow || !grid || !line) return;

    this.#gridTop = grid.getBoundingClientRect().top + scrollY;

    // Station order, not document order, or the wrong drawing lights up.
    this.#shapes = this.#panels
      .map((panel) => panel.querySelector("[data-scpd-organ-shape]"))
      .filter(Boolean);

    this.#badgeRadius = 0;
    this.#stations = this.#panels.map((panel) => {
      const badge = panel.querySelector("[data-scpd-badge]");
      if (!badge) return 0;
      const rect = badge.getBoundingClientRect();
      this.#badgeRadius = Math.max(this.#badgeRadius, rect.height / 2);
      return rect.top + scrollY + rect.height / 2;
    });

    const first = this.#panels[0];
    const last = this.#stations.at(-1);
    if (!first || !last) return;

    this.#railTop = first.getBoundingClientRect().top + scrollY;
    this.#railHeight = Math.max(0, last - this.#railTop);
    line.style.top = `${this.#railTop - this.#gridTop}px`;
    line.style.height = `${this.#railHeight}px`;

    // Measured from the badge, not the words, because the words are still nudged down, waiting to arrive, when this runs.
    const PAD = 6;
    this.#railLine = line;
    this.#railHoles = this.#panels.map((panel) => {
      const badge = panel.querySelector("[data-scpd-badge]");
      const end = panel.lastElementChild;
      if (!badge || !end) return null;
      return {
        from: badge.getBoundingClientRect().bottom + scrollY - this.#railTop - PAD,
        to: end.getBoundingClientRect().bottom + scrollY - this.#railTop + PAD,
      };
    });
    this.#maskRail(this.#reached);
  }

  #maskRail(reached) {
    if (!this.#railLine) return;
    const FADE = 10;
    const stops = ["#000 0px"];
    this.#railHoles.forEach((hole, index) => {
      if (!hole || index > reached) return;
      stops.push(`#000 ${hole.from - FADE}px`, `transparent ${hole.from}px`, `transparent ${hole.to}px`, `#000 ${hole.to + FADE}px`);
    });
    const mask = stops.length > 1 ? `linear-gradient(to bottom, ${stops.join(", ")})` : "none";
    this.#railLine.style.maskImage = mask;
    this.#railLine.style.webkitMaskImage = mask;
  }

  #lengthAt(y) {
    const first = this.#samples[0];
    const last = this.#samples.at(-1);
    if (y <= first.y) return 0;
    if (y >= last.y) return this.#length;

    let low = 0;
    let high = this.#samples.length - 1;
    while (high - low > 1) {
      const mid = (low + high) >> 1;
      if (this.#samples[mid].y <= y) low = mid;
      else high = mid;
    }

    const a = this.#samples[low];
    const b = this.#samples[high];
    const span = b.y - a.y;
    const t = span > 0 ? (y - a.y) / span : 0;
    return a.at + (b.at - a.at) * t;
  }

  write(progress, y) {
    if (this.#narrow) {
      this.#writeRail(y);
      return;
    }

    if (!this.#length || !this.#samples.length) return;

    // Slightly above the middle, because a panel writes itself out downwards from its station.
    const eyeLine = y + innerHeight * 0.45;
    const inDrawing = (eyeLine - this.#svgTop) / this.#scale;
    const head = this.#route.getPointAtLength(this.#lengthAt(inDrawing));

    if (this.#dot) {
      this.#dot.setAttribute("transform", `translate(${head.x} ${head.y})`);
    }

    // The reveal stops exactly at the dot, which keeps the two in step however the browser schedules the repaint.
    if (this.#reveal) {
      this.#reveal.setAttribute("height", String(head.y));
    }

    // A station lights as the dot enters its ring, not when it reaches the centre.
    let reached = -1;
    this.#stations.forEach((at, index) => {
      if (head.y >= at - RING) reached = index;
    });

    if (reached === this.#reached) return;
    this.#reached = reached;

    this.#light(reached);
  }

  #writeRail(y) {
    if (!this.#railHeight) return;

    const eyeLine = y + innerHeight * 0.45;

    // The fill stops exactly at the dot, which keeps the two in step however the browser schedules the repaint.
    const drawn = Math.max(0, Math.min(this.#railHeight, eyeLine - this.#railTop));
    if (this.#railFill) this.#railFill.style.height = `${drawn}px`;

    if (this.#railDot) {
      const hidden = this.#railHoles.some(
        (hole, index) => hole && index <= this.#reached && drawn > hole.from && drawn < hole.to
      );
      this.#railDot.classList.toggle("is-on", drawn > 0 && !hidden);
      this.#railDot.style.transform = `translateY(${this.#railTop + drawn - this.#gridTop}px)`;
    }

    let reached = -1;
    this.#stations.forEach((at, index) => {
      if (eyeLine >= at - this.#badgeRadius) reached = index;
    });

    if (reached === this.#reached) return;
    this.#reached = reached;
    this.#light(reached);
    this.#maskRail(reached);
  }

  #light(reached) {
    this.#shapes.forEach((shape, i) => shape.classList.toggle("is-active", i <= reached));
    this.#leaders.forEach((leader, i) => leader.classList.toggle("is-active", i <= reached));
    this.#panels.forEach((panel, i) => panel.classList.toggle("is-active", i <= reached));
  }
}

class Timeline extends ScrollTrack {
  #milestones = [];
  #claims = [];
  #ticks = [];
  #shownTicks = -1;
  #label = null;
  #note = null;
  #current = -1;

  measure() {
    this.#milestones = [...this.querySelectorAll("[data-scpd-milestone]")].map((stop) => ({
      title: stop.dataset.title ?? "",
      note: stop.dataset.note ?? "",
      pouches: Number(stop.dataset.pouches) || 1,
    }));
    this.#claims = [...this.querySelectorAll("[data-scpd-claim]")];
    this.#ticks = [...this.querySelectorAll("[data-scpd-tick]")];
    this.#shownTicks = -1;
    this.#label = this.querySelector("[data-scpd-stage-label]");
    this.#note = this.querySelector("[data-scpd-stage-note]");
    this.#current = -1;
    // The server may send the heading empty (it cannot read the milestone metaobject there), so the first title stands in.
    this.#splitLabel(this.#label?.textContent.trim() || this.#milestones[0]?.title || "");
  }

  #splitLabel(title) {
    if (!this.#label || this.#label.querySelector("[data-scpd-stage-count]")) return;
    const parts = this.#parts(title);
    if (!parts) {
      this.#label.textContent = title;
      return;
    }
    const box = (text, key) => {
      const outer = document.createElement("span");
      outer.className = "scpd-timeline__roll";
      outer.dataset[key] = "";
      const inner = document.createElement("span");
      inner.className = "scpd-timeline__roll-text";
      inner.textContent = text;
      outer.append(inner);
      return outer;
    };
    this.#label.textContent = "";
    this.#label.append(box(parts.count, "scpdStageCount"), " ", box(parts.unit, "scpdStageUnit"));
  }

  #parts(title) {
    const match = /^(\d+(?:[.,]\d+)?)\s+(.+)$/.exec(title);
    return match ? { count: match[1], unit: match[2] } : null;
  }

  write(progress) {
    const count = this.#milestones.length;
    if (!count) return;

    // Everything happens in the first 80% of the track, so the stage holds still for a moment before it lets go.
    const played = clamp01(progress / 0.8);

    const reached = Math.min(count - 1, Math.floor(played * count + 0.7) - 1);
    const current = Math.max(0, reached);

    this.#claims.forEach((claim) => {
      const at = Number(claim.dataset.at);
      claim.classList.toggle("is-on", at <= reached);
      claim.classList.toggle("is-current", at === current);
    });

    const lit = this.#milestones[current].pouches;
    if (lit !== this.#shownTicks) {
      const before = Math.max(0, this.#shownTicks);
      this.#ticks.forEach((tick, index) => {
        const on = index < lit;
        if (on && !tick.classList.contains("is-on")) {
          tick.style.setProperty("--scpd-wave", String(index - before));
        }
        tick.classList.toggle("is-on", on);
      });
      this.#shownTicks = lit;
    }

    if (current === this.#current) return;
    this.#current = current;

    const stop = this.#milestones[current];
    const countBox = this.#label?.querySelector("[data-scpd-stage-count]");
    const unitBox = this.#label?.querySelector("[data-scpd-stage-unit]");
    const parts = this.#parts(stop.title);
    if (countBox && unitBox && parts) {
      this.#roll(countBox, parts.count);
      this.#roll(unitBox, parts.unit);
    } else {
      this.#swap(this.#label, stop.title);
    }
    this.#swap(this.#note, stop.note);
  }

  #roll(box, text) {
    const texts = [...box.querySelectorAll(".scpd-timeline__roll-text")];
    const current = texts.at(-1);
    if (!current || current.textContent === text) return;

    box.getAnimations({ subtree: true }).forEach((animation) => animation.cancel());
    texts.slice(0, -1).forEach((old) => old.remove());
    current.classList.remove("is-leaving");

    if (REDUCED.matches) {
      current.textContent = text;
      return;
    }

    const from = box.getBoundingClientRect().width;
    const next = document.createElement("span");
    next.className = "scpd-timeline__roll-text";
    next.textContent = text;
    current.classList.add("is-leaving");
    box.append(next);
    const to = next.getBoundingClientRect().width;

    const timing = { duration: 800, easing: "cubic-bezier(0.65, 0, 0.35, 1)" };
    const leave = current.animate(
      [
        { transform: "translateY(0)", opacity: 1 },
        { opacity: 0, offset: 0.5 },
        { transform: "translateY(-100%)", opacity: 0 },
      ],
      { ...timing, fill: "forwards" }
    );
    next.animate(
      [
        { transform: "translateY(100%)", opacity: 0 },
        { opacity: 0, offset: 0.35 },
        { transform: "translateY(0)", opacity: 1 },
      ],
      timing
    );
    box.animate([{ width: `${from}px` }, { width: `${to}px` }], timing);
    leave.onfinish = () => current.remove();
  }

  #swap(element, text) {
    if (!element || element.textContent === text) return;
    if (REDUCED.matches) {
      element.textContent = text;
      return;
    }
    element.getAnimations().forEach((animation) => animation.cancel());
    const out = element.animate(
      [
        { opacity: 1, transform: "none", filter: "blur(0)" },
        { opacity: 0, transform: "translateY(-0.18em)", filter: "blur(4px)" },
      ],
      { duration: 260, easing: "cubic-bezier(0.4, 0, 1, 1)", fill: "forwards" }
    );
    out.onfinish = () => {
      const from = element.getBoundingClientRect().width;
      element.textContent = text;
      const to = element.getBoundingClientRect().width;
      out.cancel();
      element.animate(
        [
          { opacity: 0, transform: "translateY(0.22em)", filter: "blur(4px)", width: `${from}px` },
          { opacity: 1, transform: "none", filter: "blur(0)", width: `${to}px` },
        ],
        { duration: 750, easing: "cubic-bezier(0.16, 1, 0.3, 1)" }
      );
    };
  }
}

class Flavours extends HTMLElement {
  #panels = [];

  connectedCallback() {
    this.#panels = [...this.querySelectorAll("[data-scpd-flavour]")];
    this.addEventListener("click", this.#onClick);
  }

  #onClick = (event) => {
    const panel = event.target.closest("[data-scpd-flavour]");
    if (!panel) return;

    // Opening a card deliberately leaves the variant, price and buy bar alone: browsing must never change what is being bought.
    this.#open(panel.dataset.scpdFlavour);
  };

  #open(value) {
    for (const panel of this.#panels) {
      const on = panel.dataset.scpdFlavour === value;
      panel.classList.toggle("is-chosen", on);
      panel.setAttribute("aria-pressed", String(on));
    }
  }
}


if (!customElements.get("scpd-flavours")) {
  customElements.define("scpd-flavours", Flavours);
}

// The line is scaled rather than resized, so the growing happens on the compositor.
class Plan extends ScrollTrack {
  #line = null;
  #perks = [];
  #marks = [];
  #reached = -2;

  measure() {
    this.#line = this.querySelector("[data-scpd-line]");
    this.#perks = [...this.querySelectorAll("[data-scpd-perk]")];

    const rail = this.querySelector("[data-scpd-rail]");
    if (!rail) return;

    const box = rail.getBoundingClientRect();
    this.#marks = this.#perks.map((perk) => {
      const bead = perk.querySelector("[data-scpd-bead]");
      const at = bead ? bead.getBoundingClientRect() : perk.getBoundingClientRect();
      return box.height > 0 ? (at.top + at.height / 2 - box.top) / box.height : 0;
    });

    this.#reached = -2;
  }

  write(progress) {
    if (this.#line) this.#line.style.setProperty("--scpd-drawn", progress.toFixed(4));

    let reached = -1;
    this.#marks.forEach((at, index) => {
      if (progress >= at) reached = index;
    });

    if (reached === this.#reached) return;
    this.#reached = reached;
    this.#perks.forEach((perk, i) => perk.classList.toggle("is-reached", i <= reached));
  }
}

// Every button inside is wired, because a carousel can hold more than one pair.
// Scrolling is the browser's own (`scroll-behavior: smooth`), so a swipe and a button press behave identically.
class Carousel extends HTMLElement {
  #rail = null;
  #prev = [];
  #next = [];
  #count = null;
  #size = null;

  connectedCallback() {
    this.#rail = this.querySelector("[data-scpd-track]");
    this.#prev = [...this.querySelectorAll("[data-scpd-prev]")];
    this.#next = [...this.querySelectorAll("[data-scpd-next]")];
    this.#count = this.querySelector("[data-scpd-count-current]");
    if (!this.#rail) return;

    this.#prev.forEach((button) => button.addEventListener("click", () => this.#step(-1)));
    this.#next.forEach((button) => button.addEventListener("click", () => this.#step(1)));
    this.#rail.addEventListener("scroll", this.#sync, { passive: true });
    addEventListener("resize", this.#sync, { passive: true });
    // A rail can connect before it has a size (inside a hidden section or a closed panel),
    // so it is measured again whenever its own size changes.
    this.#size = new ResizeObserver(this.#sync);
    this.#size.observe(this.#rail);
    this.#sync();
  }

  disconnectedCallback() {
    this.#rail?.removeEventListener("scroll", this.#sync);
    removeEventListener("resize", this.#sync);
    this.#size?.disconnect();
  }

  #step(direction) {
    const card = this.#rail.firstElementChild;
    if (!card) return;

    const gap = Number.parseFloat(getComputedStyle(this.#rail).columnGap) || 0;
    const by = card.getBoundingClientRect().width + gap;
    this.#rail.scrollBy({ left: by * direction });
  }

  #sync = () => {
    if (!this.#rail) return;
    // A pixel of slack: a scroll position is rarely a whole number.
    const end = this.#rail.scrollWidth - this.#rail.clientWidth - 1;
    this.#prev.forEach((button) => (button.disabled = this.#rail.scrollLeft <= 1));
    this.#next.forEach((button) => (button.disabled = this.#rail.scrollLeft >= end));

    if (this.#count) {
      const card = this.#rail.firstElementChild;
      const gap = Number.parseFloat(getComputedStyle(this.#rail).columnGap) || 0;
      const by = card ? card.getBoundingClientRect().width + gap : 0;
      const cards = this.#rail.children.length;
      // At the very end the last card may never reach the start, so the count finishes on it.
      const index = this.#rail.scrollLeft >= end ? cards - 1 : by ? Math.round(this.#rail.scrollLeft / by) : 0;
      this.#count.textContent = String(Math.min(cards, index + 1)).padStart(2, "0");
    }
  };
}

// `<details>` has no height to animate, so the row opens first and then grows to its measured height.
// `single` closes siblings because the hero's sheet has no room for every row open at once.
class Accordion extends HTMLElement {
  #details = null;
  #summary = null;
  #body = null;
  #inner = null;
  #settle = null;
  #timer = 0;

  static DURATION = 450;

  connectedCallback() {
    this.#details = this.querySelector("details");
    this.#summary = this.querySelector("summary");
    this.#body = this.querySelector("[data-scpd-body]");
    this.#inner = this.#body?.firstElementChild;
    if (!this.#details || !this.#summary || !this.#body) return;

    this.#summary.addEventListener("click", this.#onClick);
  }

  disconnectedCallback() {
    this.#summary?.removeEventListener("click", this.#onClick);
  }

  get isOpen() {
    return this.#details?.open ?? false;
  }

  #onClick = (event) => {
    // Take the toggle off `<details>`, so the height can animate between two known values.
    event.preventDefault();
    if (this.isOpen) this.close();
    else this.open();
  };

  open() {
    if (!this.#details || this.isOpen) return;

    if (this.hasAttribute("single")) {
      for (const other of this.parentElement?.children ?? []) {
        if (other !== this && other.close) other.close();
      }
    }

    this.#details.open = true;
    this.setAttribute("open", "");

    if (REDUCED.matches) return;
    this.#animate(0, this.#inner?.offsetHeight ?? 0, () => {
      // Back to auto, so the row follows its content if that changes later.
      this.#body.style.height = "";
    });
  }

  close() {
    if (!this.#details || !this.isOpen) return;
    this.removeAttribute("open");

    if (REDUCED.matches) {
      this.#details.open = false;
      return;
    }

    this.#animate(this.#body.offsetHeight, 0, () => {
      this.#details.open = false;
      this.#body.style.height = "";
    });
  }

  #animate(from, to, done) {
    this.#body.style.height = `${from}px`;
    // Reading the height commits the starting value, so the browser has two values to move between.
    void this.#body.offsetHeight;
    this.#body.style.height = `${to}px`;

    // Scroll anchoring moves the page while the row changes height, and Lenis then pulls it back: a visible jolt.
    const root = document.documentElement;
    root.style.overflowAnchor = "none";
    clearTimeout(this.#timer);
    this.#body.removeEventListener("transitionend", this.#settle);

    this.#settle = (event) => {
      if (event && (event.target !== this.#body || event.propertyName !== "height")) return;
      this.#body.removeEventListener("transitionend", this.#settle);
      clearTimeout(this.#timer);
      root.style.overflowAnchor = "";
      done();
    };
    this.#body.addEventListener("transitionend", this.#settle);
    // transitionend never fires when a transition is cut short.
    this.#timer = setTimeout(this.#settle, Accordion.DURATION + 100);
  }
}

if (!customElements.get("scpd-accordion")) {
  customElements.define("scpd-accordion", Accordion);
}

if (!customElements.get("scpd-carousel")) {
  customElements.define("scpd-carousel", Carousel);
}

// Clicks the hero's own Subscribe option, so the button price and the hidden plan field follow as usual.
document.addEventListener("click", (event) => {
  const start = event.target.closest("[data-scpd-start-plan] a, [data-scpd-start-plan] button");
  if (!start) return;
  const hero = document.querySelector("scpd-pdp-hero");
  if (!hero) return;
  event.preventDefault();

  const subscribe = hero.querySelector('[data-scpd-purchase]:not([data-scpd-purchase="once"])');
  if (subscribe && subscribe.getAttribute("aria-pressed") !== "true") subscribe.click();

  const target = hero.getBoundingClientRect().top + scrollY;
  const lenis = getDriver().lenis;
  if (lenis) lenis.scrollTo(target);
  else scrollTo({ top: target, behavior: "smooth" });
});

if (!customElements.get("scpd-plan")) {
  customElements.define("scpd-plan", Plan);
}

if (!customElements.get("scpd-timeline")) {
  customElements.define("scpd-timeline", Timeline);
}

if (!customElements.get("scpd-intake")) {
  customElements.define("scpd-intake", Intake);
}

if (!customElements.get("scpd-select")) {
  customElements.define("scpd-select", OptionSelect);
}

if (!customElements.get("scpd-pdp-hero")) {
  customElements.define("scpd-pdp-hero", PdpHero);
}

// Appearing is left to an `IntersectionObserver`, not a scroll handler, because the page already does pinned work every frame.
class BuyBar extends HTMLElement {
  #io = null;
  #footerIo = null;
  #clearIo = null;
  #pastHero = false;
  #atFooter = false;
  #covered = new Set();
  #narrow = matchMedia("(max-width: 899px)");
  #wide = matchMedia("(min-width: 990px)");
  #dock = null;

  connectedCallback() {
    document.addEventListener("scpd:variant", this.#onVariant);
    this.addEventListener("click", this.#onClick, true);

    this.#dock = document.querySelector(".scpd-pdp--editorial .scpd-pdp__dock");
    this.#wide.addEventListener("change", this.#update);

    const anchor = document.querySelector("scpd-pdp-hero");

    if (!anchor) {
      this.#pastHero = true;
    } else {
      this.#io = new IntersectionObserver(
        ([entry]) => {
          this.#pastHero = !entry.isIntersecting && entry.boundingClientRect.top < 0;
          this.#update();
        },
        { rootMargin: "0px 0px -45% 0px", threshold: 0 }
      );
      this.#io.observe(anchor);
    }

    const footer = document.querySelector(".shopify-section-group-footer-group");
    if (footer) {
      this.#footerIo = new IntersectionObserver(
        ([entry]) => {
          this.#atFooter = entry.isIntersecting;
          this.#update();
        },
        { threshold: 0 }
      );
      this.#footerIo.observe(footer);
    }

    const clear = document.querySelectorAll("[data-scpd-hide-bar]");
    if (clear.length) {
      this.#clearIo = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) =>
            entry.isIntersecting ? this.#covered.add(entry.target) : this.#covered.delete(entry.target)
          );
          this.#update();
        },
        { rootMargin: "-85% 0px 0px 0px", threshold: 0 }
      );
      clear.forEach((el) => this.#clearIo.observe(el));
      this.#narrow.addEventListener("change", this.#update);
    }

    this.#update();
  }

  #update = () => {
    const covered = this.#narrow.matches && this.#covered.size > 0;
    const docked = Boolean(this.#dock) && this.#wide.matches;
    this.#show(!docked && this.#pastHero && !this.#atFooter && !covered);
  };

  disconnectedCallback() {
    document.removeEventListener("scpd:variant", this.#onVariant);
    this.removeEventListener("click", this.#onClick, true);
    this.#io?.disconnect();
    this.#footerIo?.disconnect();
    this.#clearIo?.disconnect();
    this.#narrow.removeEventListener("change", this.#update);
    this.#wide.removeEventListener("change", this.#update);
  }

  #show(on) {
    this.classList.toggle("is-visible", on);
    // Parked off screen it would still be in the tab order, putting an invisible buy button in the reader's way.
    this.toggleAttribute("inert", !on);
  }

  get #buy() {
    return this.querySelector(".scpd-buy-bar__action--buy");
  }

  #onVariant = ({ detail: variant }) => {
    const buy = this.#buy;
    const sub = this.querySelector(".scpd-buy-bar__action--sub");

    if (!variant) {
      if (buy) {
        buy.disabled = true;
        setLabel(buy, this.dataset.unavailableLabel || "Unavailable");
      }
      if (sub) sub.disabled = true;
      return;
    }

    const field = this.querySelector('[ref="variantId"]');
    if (field) field.value = variant.id;

    const price = ref(this, "bar-price");
    if (price) swapText(price, variant.price);

    const note = ref(this, "bar-variant");
    if (note) {
      const order = (this.dataset.noteOrder || "")
        .split(",")
        .map(Number)
        .filter(Boolean);
      const parts = order.length
        ? order.map((position) => variant.options[position - 1])
        : variant.options;
      swapText(note, parts.filter(Boolean).join(", "));
    }

    if (buy) {
      buy.disabled = !variant.available;
      setLabel(
        buy,
        variant.available ? this.dataset.buyLabel : this.dataset.soldoutLabel || this.dataset.buyLabel
      );
    }
    if (sub) {
      sub.disabled = !variant.available;
      if (variant.planId) sub.dataset.scpdPlan = variant.planId;
      const gap = parseFloat(getComputedStyle(sub.parentElement).columnGap) || 0;
      fold(sub, Boolean(variant.planPrice), gap);
    }
  };

  #onClick = (event) => {
    const back = event.target.closest("[data-scpd-back]");
    if (back) {
      const hero = document.querySelector("scpd-pdp-hero");
      if (!hero) return;
      const target = hero.getBoundingClientRect().top + scrollY;
      const lenis = getDriver().lenis;
      if (lenis) lenis.scrollTo(target);
      else scrollTo({ top: target, behavior: "smooth" });
      return;
    }

    // A submit carrying a plan is the subscription purchase, so the hidden field is filled or emptied on the way in.
    const submit = event.target.closest("[type='submit']");
    if (!submit) return;
    const plan = ref(this, "bar-selling-plan");
    if (plan) plan.value = submit.dataset.scpdPlan || "";
  };
}

class Comparison extends HTMLElement {
  connectedCallback() {
    this.addEventListener("click", this.#onClick);
  }

  disconnectedCallback() {
    this.removeEventListener("click", this.#onClick);
  }

  #onClick = (event) => {
    const chip = event.target.closest("[data-scpd-compare]");
    if (!chip || chip.classList.contains("is-chosen")) return;
    const column = chip.dataset.scpdCompare;

    for (const other of this.querySelectorAll("[data-scpd-compare]")) {
      const on = other === chip;
      other.classList.toggle("is-chosen", on);
      other.setAttribute("aria-pressed", String(on));
    }

    for (const cell of this.querySelectorAll("[data-scpd-col]")) {
      cell.classList.toggle("is-compared", cell.dataset.scpdCol === column);
    }
  };
}

if (!customElements.get("scpd-comparison")) {
  customElements.define("scpd-comparison", Comparison);
}

if (!customElements.get("scpd-buy-bar")) {
  customElements.define("scpd-buy-bar", BuyBar);
}

// Signs the reader up without leaving the page. Shopify answers a customer form with a redirect, which
// tells success apart from a failure; anything unexpected, such as its spam check, falls back to a
// normal submit so the page can show it.
class Waitlist extends HTMLElement {
  connectedCallback() {
    this.addEventListener("submit", this.#onSubmit);
    this.addEventListener("click", this.#onClick, true);
    this.addEventListener("input", this.#onInput);
    this.addEventListener("invalid", this.#onInvalid, true);
    document.addEventListener("scpd:waitlist-joined", this.#onJoined);
    for (const field of this.#fields) this.#fit(field);
    this.addEventListener("change", this.#onChoice);
    this.#markListed();
    this.#syncChoices();

    // Each form checks for itself: the one further down the page connects after an event would have gone.
    // After a normal submit Shopify returns to `?customer_posted=true#<form id>`; only a waitlist form counts.
    const posted = new URLSearchParams(location.search).get("customer_posted") === "true";
    const ours = [...document.querySelectorAll("scpd-waitlist form")].some((form) => location.hash === `#${form.id}`);
    // With a product pick only the products count: the form is done once every product is on the list.
    const choices = this.#choices;
    const done = choices.length ? choices.every((choice) => choice.disabled) : this.#remembered;
    if ((posted && ours) || done) this.#onJoined({ instant: true });
  }

  #listed(id) {
    try {
      return localStorage.getItem(`scpd-waitlist-${id}`) === "1";
    } catch {
      return false;
    }
  }

  // A product the reader already waits for (signed up here or on its own page) shows ticked and locked,
  // so the pick only offers what is new and the cards still say what the reader is on the list for.
  #markListed() {
    for (const choice of this.#choices) {
      const listed = this.#listed(choice.value);
      if (listed) choice.checked = true;
      choice.disabled = listed;
    }
  }

  get #key() {
    return `scpd-waitlist-${this.dataset.productId}`;
  }

  // Kept in this browser, so a later visit to the same product still says the reader is on the list.
  get #remembered() {
    try {
      return localStorage.getItem(this.#key) === "1";
    } catch {
      return false;
    }
  }

  // A pick of products counts on each product's own page too. Across Shopify's challenge page the pick
  // only survives in the session, so it is written down before the form leaves.
  #remember() {
    try {
      localStorage.setItem(this.#key, "1");
      const picked = this.#choices.length ? JSON.parse(sessionStorage.getItem("scpd-waitlist-picked") || "[]") : [];
      for (const id of [...picked, ...this.#picked]) localStorage.setItem(`scpd-waitlist-${id}`, "1");
      sessionStorage.removeItem("scpd-waitlist-picked");
    } catch {}
    window.scpdMarkListed?.();
  }

  get #choices() {
    return [...this.querySelectorAll("[data-scpd-waitlist-choice]")];
  }

  get #picked() {
    return this.#choices.filter((choice) => choice.checked && !choice.disabled).map((choice) => choice.value);
  }

  // Written on every pick, not on submit: when Shopify's CAPTCHA takes the submit, the submit handler never runs.
  #onChoice = (event) => {
    if (!event.target.matches("[data-scpd-waitlist-choice]")) return;
    this.#syncChoices();
    try {
      sessionStorage.setItem("scpd-waitlist-picked", JSON.stringify(this.#picked));
    } catch {}
  };

  // The tags follow the pick, and the first pill carries the "pick one" error while none is picked.
  #syncChoices() {
    const choices = this.#choices;
    const tags = this.querySelector("[data-scpd-waitlist-tags]");
    if (!choices.length || !tags) return;
    const open = choices.filter((choice) => !choice.disabled);
    const picked = open.filter((choice) => choice.checked);
    tags.value = ["waitlist", ...picked.map((choice) => choice.dataset.tags)].join(",");
    if (open.length < choices.length) this.#rest(!picked.length);
    if (!open.length) return;
    open[0].setCustomValidity(picked.length ? "" : this.dataset.errorChoice || "Pick at least one product");
    if (picked.length) this.#clearError();
  }

  // Once a product is on the list the form rests on "You're on the list"; picking a product that is not
  // on it yet opens the fields again.
  #rest(resting) {
    if (this.classList.contains("is-joined") === resting) return;
    const button = this.querySelector("[type='submit']");
    const label = button?.querySelector(".scpd-buy__label");
    if (!button) return;
    this.#label ??= label?.textContent.trim();
    this.classList.toggle("is-joined", resting);
    button.classList.toggle("is-added", resting);
    button.disabled = resting;
    for (const field of this.#fields) field.disabled = resting;
    rollText(label, resting ? this.dataset.thanks : this.#label);
  }

  #label;

  disconnectedCallback() {
    this.removeEventListener("submit", this.#onSubmit);
    this.removeEventListener("click", this.#onClick, true);
    this.removeEventListener("input", this.#onInput);
    this.removeEventListener("invalid", this.#onInvalid, true);
    this.removeEventListener("change", this.#onChoice);
    document.removeEventListener("scpd:waitlist-joined", this.#onJoined);
  }

  get #fields() {
    return [...this.querySelectorAll(".scpd-waitlist__field")];
  }

  // `size` sets the width in characters; the stylesheet only lets it count on the one-line form.
  // The email field starts wider than its placeholder, because an address is longer than "Your email".
  #fit(field) {
    const min = Number(field.dataset.scpdMinSize) || 0;
    field.size = Math.max(min, field.placeholder.length, field.value.length) + 1;
  }

  #onInput = (event) => {
    if (!event.target.matches("input:not([type='hidden'])")) return;
    if (event.target.matches(".scpd-waitlist__field")) this.#fit(event.target);
    if (event.target.classList.contains("is-invalid")) this.#clearError();
  };

  // Instead of the browser's bubble, the first failing field of a submit turns its line burgundy and takes the focus.
  #onInvalid = (event) => {
    event.preventDefault();
    if (this.#erroring) return;
    this.#erroring = true;
    requestAnimationFrame(() => (this.#erroring = false));
    this.#clearError();
    event.target.classList.add("is-invalid");
    event.target.focus({ preventScroll: true });
    this.#showError(event.target);
  };

  // Names what is missing; on the one-line pill the message floats above the field it is about.
  #showError(field) {
    const error = this.querySelector("[data-scpd-waitlist-error]");
    if (!error) return;
    const { errorName, errorEmail, errorEmailInvalid, errorConsent, errorChoice } = this.dataset;
    if (field.matches("[data-scpd-waitlist-choice]")) error.textContent = errorChoice;
    else if (field.type === "checkbox") error.textContent = errorConsent;
    else if (field.type === "email") error.textContent = field.validity.valueMissing ? errorEmail : errorEmailInvalid;
    else error.textContent = errorName;
    error.hidden = false;

    const anchor = field.closest("fieldset") || field.closest("label") || field;
    const frame = error.offsetParent?.getBoundingClientRect();
    if (frame) error.style.setProperty("--scpd-waitlist-error-x", `${anchor.getBoundingClientRect().left - frame.left}px`);
  }

  #erroring = false;

  #clearError() {
    for (const field of this.querySelectorAll(".is-invalid")) field.classList.remove("is-invalid");
    const error = this.querySelector("[data-scpd-waitlist-error]");
    if (error) error.hidden = true;
  }

  // Where the fields are folded away (the floating bar on a phone), the button takes the reader to the
  // hero's form instead; it runs before submitting, because the browser would reject the hidden empty fields.
  #onClick = (event) => {
    const button = event.target.closest("[type='submit']");
    const field = this.querySelector("input[type='email']");
    if (!button || !field || field.offsetParent !== null) return;
    const other = [...document.querySelectorAll("scpd-waitlist")].find(
      (host) => host !== this && host.querySelector("input[type='email']")?.offsetParent !== null
    );
    if (!other) return;
    event.preventDefault();
    const target = other.getBoundingClientRect().top + scrollY - innerHeight / 3;
    const lenis = getDriver().lenis;
    if (lenis) lenis.scrollTo(target);
    else scrollTo({ top: target, behavior: "smooth" });
    other.querySelector("input:not([type='hidden'])")?.focus({ preventScroll: true });
  };

  // Set while the form is handed back to the browser, so this handler lets that submit through.
  #native = false;

  #onSubmit = async (event) => {
    if (this.#native) {
      this.#native = false;
      return;
    }
    if (event.defaultPrevented) return;
    const form = event.target;
    const button = form.querySelector("[type='submit']");
    if (!button || button.disabled) return;
    event.preventDefault();

    try {
      sessionStorage.setItem("scpd-waitlist-picked", JSON.stringify(this.#picked));
    } catch {}
    button.classList.add("is-adding");
    button.disabled = true;
    try {
      const response = await fetch(form.action, {
        method: "POST",
        body: new URLSearchParams(new FormData(form)),
      });
      if (!response.url.includes("customer_posted=true")) throw new Error("not joined");
      document.dispatchEvent(new CustomEvent("scpd:waitlist-joined", { detail: { form } }));
    } catch {
      // Not `form.submit()`: it skips every submit listener, including Shopify's CAPTCHA, which it turns on
      // after a burst of sign-ups; the post then arrives without a token and Shopify shows an error page.
      button.disabled = false;
      this.#native = true;
      form.requestSubmit(button);
    } finally {
      button.classList.remove("is-adding");
    }
  };

  // Every sign-up on the page agrees, so the dock and the floating bar never disagree.
  #onJoined = async (event) => {
    this.#remember();
    this.#clearError();

    // With a product pick the form rests while a product is left to join, and the joined ones show ticked.
    if (this.#choices.length) {
      this.#markListed();
      const open = this.#choices.filter((choice) => !choice.disabled);
      if (open.length) {
        for (const choice of open) choice.checked = false;
        this.querySelector("[type='submit']")?.classList.remove("is-adding");
        this.#syncChoices();
        const status = this.querySelector("[data-scpd-waitlist-status]");
        if (status) status.textContent = this.dataset.thanks;
        return;
      }
    }

    const button = this.querySelector("[type='submit']");
    const status = this.querySelector("[data-scpd-waitlist-status]");
    const thanks = this.dataset.thanks;
    if (!button || this.classList.contains("is-joined")) return;
    button.disabled = true;
    button.classList.add("is-added");
    for (const field of this.#fields) field.disabled = true;
    if (status) status.textContent = thanks;

    const leaving = [...this.querySelectorAll(".scpd-waitlist__field, .scpd-waitlist__rule, .scpd-waitlist__consent")].filter(
      (el) => el.offsetParent !== null
    );
    if (event?.instant || REDUCED.matches || !leaving.length) {
      this.classList.add("is-joined");
      rollText(button.querySelector(".scpd-buy__label"), thanks);
      return;
    }

    await Promise.all(
      leaving.map(
        (el) =>
          el.animate([{ opacity: 1 }, { opacity: 0, filter: "blur(0.4em)" }], {
            duration: 280,
            easing: "ease-out",
            fill: "forwards",
          }).finished
      )
    );

    // The fields leave at once, so the box is played from its old size to its new one. Space-between keeps
    // the label and the button at the ends while the room between them closes.
    const form = this.querySelector("form");
    const from = this.getBoundingClientRect();
    this.classList.add("is-joined");
    const to = this.getBoundingClientRect();
    rollText(button.querySelector(".scpd-buy__label"), thanks);
    form.style.justifyContent = "space-between";
    const resize = this.animate(
      [
        { width: `${from.width}px`, height: `${from.height}px` },
        { width: `${to.width}px`, height: `${to.height}px` },
      ],
      { duration: 700, easing: "cubic-bezier(0.16, 1, 0.3, 1)" }
    );
    await resize.finished.catch(() => {});
    form.style.justifyContent = "";
    leaving.forEach((el) => el.getAnimations().forEach((animation) => animation.cancel()));
  };
}

if (!customElements.get("scpd-waitlist")) {
  customElements.define("scpd-waitlist", Waitlist);
}

